import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../../Contexts/CartContext';
import { useAuth } from '../../hooks/useAuth';
import { useOrderStatus } from '../../hooks/useOrderStatus';
import ReviewModal from '../../components/Review/ReviewModal';
import { orderTrackingModule } from '../../api/modules/orderTracking.module';
import { showToast } from '../../utils/toast';
import { track } from '../../analytics/track';
import { EVENTS } from '../../analytics/events';
import { PaymentSuccessView } from './PaymentSuccess.view';

/**
 * Payment success, the container. Verification, polling, the confirm budget,
 * the cart clear, analytics, the review modal and the redirect timer.
 *
 * Everything visual lives in `PaymentSuccess.view.jsx`, which is pure, so the
 * state explorer can mount the real screen without any of this running. See
 * `design-system/explorer/registry.js` for that convention.
 *
 * `PaymentSuccess.css` is gone: nothing else imported it and the page no longer
 * emits one of its class names.
 */

// How long we keep polling before admitting we can't confirm yet. The order
// isn't lost, the dashboard will surface it as "Payment processing", this
// just stops the success page from waiting on this screen forever.
const CONFIRM_BUDGET_MS = 60000;

/** Seconds on the auto-redirect readout, and on the timer it reads out. */
const REDIRECT_SECONDS = 5;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { removeSellerItems, reloadCart } = useCart();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const { status, isConfirmed } = useOrderStatus(orderId);
  const hasCleared = useRef(false); // Prevent multiple clears
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  // Captured from `pendingOrderInfo` BEFORE the clear effect removes it, so the
  // receipt survives the cleanup that happens in the same pass.
  const [receipt, setReceipt] = useState(null);
  const [redirectIn, setRedirectIn] = useState(null);

  // Fire-and-observe payment verification: the backend probes Yoco directly,
  // so confirmation doesn't depend on the webhook pipe (which is exactly what
  // broke in the double-payment incident). One retry covers a probe that races
  // the redirect; the 2s order-status polling picks up the settled state.
  useEffect(() => {
    if (!orderId) return undefined;
    let cancelled = false;
    let retryTimer;
    orderTrackingModule.verifyPayment(orderId).then((res) => {
      if (cancelled || res?.verified || res?.status === 'paid' || res?.status === 'delivered')
        return;
      retryTimer = setTimeout(() => {
        if (!cancelled) orderTrackingModule.verifyPayment(orderId);
      }, 5000);
    });
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [orderId]);

  // Honest ~60s budget: if the backend never confirms the order in time,
  // stop waiting and say so instead of silently sitting on "Confirming".
  useEffect(() => {
    if (isConfirmed) return;
    const timer = setTimeout(() => setTimedOut(true), CONFIRM_BUDGET_MS);
    return () => clearTimeout(timer);
  }, [isConfirmed]);

  // Clear the cart / show the in-person review flow only once the backend
  // confirms the order is paid or delivered, never on a timer alone.
  useEffect(() => {
    if (!isConfirmed || hasCleared.current) {
      return;
    }

    const pendingOrderInfo = JSON.parse(localStorage.getItem('pendingOrderInfo'));

    if (pendingOrderInfo && pendingOrderInfo.sellerId) {
      const amount = (pendingOrderInfo.storeItems || []).reduce(
        (s, it) => s + (it.card_listing?.price || 0) * (it.quantity || 0),
        0
      );
      track(EVENTS.PAYMENT_SUCCESS, {
        order_id: pendingOrderInfo.orderId,
        amount,
        seller_id: pendingOrderInfo.sellerId,
        is_in_person: pendingOrderInfo.isInPerson || false,
      });

      // The two facts a buyer looks for after paying. Read HERE because this is
      // the last moment `pendingOrderInfo` still exists.
      const receiptOrderId = pendingOrderInfo.orderId || orderId;
      setReceipt({
        orderNumber: receiptOrderId ? `#${receiptOrderId}` : null,
        amount: amount > 0 ? amount : null,
      });

      // Remove only the purchased items from this specific seller. The cart
      // context resolves the storage key itself (by user, or "cart_guest"), so
      // there is no cross-user localStorage scan or fallback here.
      removeSellerItems(pendingOrderInfo.sellerId);
      reloadCart();

      // Delivered (confirmed by the courier) or in-person purchases (never
      // get a delivered webhook) both trigger the review flow.
      if (status === 'delivered' || pendingOrderInfo.isInPerson) {
        const sellerName =
          pendingOrderInfo.storeItems?.[0]?.card_listing?.seller?.store_name ||
          pendingOrderInfo.storeItems?.[0]?.card_listing?.seller?.full_name ||
          pendingOrderInfo.storeItems?.[0]?.card_listing?.seller?.first_name ||
          `Seller ${pendingOrderInfo.sellerId}`;

        setReviewData({
          sellerId: pendingOrderInfo.sellerId,
          orderId: pendingOrderInfo.orderId,
          sellerName,
        });

        setTimeout(() => {
          setShowReviewModal(true);
        }, 1000);
      }
    }

    // Clear orderItems for this specific checkout session so the same items
    // can't be checked out again.
    localStorage.removeItem('orderItems_guest');
    if (user) {
      localStorage.removeItem(`orderItems_${user.id || user._id}`);
    }

    // Clear order lock to allow new orders.
    localStorage.removeItem(`order_lock_${user?.id || user?._id || 'guest'}`);

    // Clear pending order info.
    localStorage.removeItem('pendingOrderInfo');

    hasCleared.current = true;
  }, [isConfirmed, status, user, orderId, removeSellerItems, reloadCart]);

  // Handle review submission
  const handleReviewSubmit = async ({ rating, comment, orderId: reviewOrderId }) => {
    try {
      const payload = {
        rating: Number(rating),
        review: String(comment || '').trim(),
        order_id: reviewOrderId,
      };

      const response = await orderTrackingModule.submitRating(payload);

      if (response.success) {
        showToast.success(response.message || 'Review submitted successfully!');
        return Promise.resolve();
      } else {
        showToast.error(response.message || 'Failed to submit review');
        throw new Error(response.message || 'Failed to submit review');
      }
    } catch (error) {
      showToast.error(error.message || 'Failed to submit review. Please try again.');
      throw error;
    }
  };

  const handleReviewClose = () => {
    setShowReviewModal(false);
    // Redirect to dashboard after closing review modal
    setTimeout(() => {
      navigate('/dashboard');
    }, 500);
  };

  // Separate effect for redirect. The timer is only armed once the order is
  // confirmed OR the ~60s confirm budget has elapsed (timedOut), never while
  // still pending and within budget. Arming it unconditionally used to
  // unmount this page at a flat 5s, which killed polling before a slow
  // webhook could flip the order and defeated the M5 cart-clear. Only
  // redirect automatically if no review modal is shown.
  useEffect(() => {
    if (!isConfirmed && !timedOut) {
      return undefined;
    }

    const timer = setTimeout(() => {
      if (!showReviewModal && !reviewData) {
        navigate('/dashboard');
      }
    }, REDIRECT_SECONDS * 1000);

    return () => clearTimeout(timer);
  }, [isConfirmed, timedOut, navigate, showReviewModal, reviewData]);

  // The readout for the timer above. It is a state announcement, never the way
  // out: the buttons always work. A number that changes every second is motion
  // too, so under `prefers-reduced-motion` it is simply not shown.
  useEffect(() => {
    if ((!isConfirmed && !timedOut) || showReviewModal || reviewData) {
      setRedirectIn(null);
      return undefined;
    }
    if (prefersReducedMotion()) {
      return undefined;
    }
    setRedirectIn(REDIRECT_SECONDS);
    const ticker = setInterval(() => {
      setRedirectIn((seconds) => (seconds == null ? null : Math.max(0, seconds - 1)));
    }, 1000);
    return () => clearInterval(ticker);
  }, [isConfirmed, timedOut, showReviewModal, reviewData]);

  return (
    <>
      <PaymentSuccessView
        confirmed={isConfirmed}
        timedOut={timedOut}
        receipt={receipt}
        redirectIn={redirectIn}
      />

      {/* Review Modal for In-Person Purchases */}
      {reviewData && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={handleReviewClose}
          onSubmit={handleReviewSubmit}
          sellerName={reviewData.sellerName}
          orderId={reviewData.orderId}
        />
      )}
    </>
  );
};

export default PaymentSuccess;
